/**
 * Help ID Constants for the Context-Aware Help System
 *
 * These IDs are used as `data-help-id` attributes on interactive UI elements
 * throughout the application. They serve as stable selectors for the Spotlight
 * Highlighting System, enabling contextual help overlays and guided tours.
 *
 * ## Naming Convention
 *
 * IDs follow the pattern: `{page}.{section}.{element}`
 *
 * - **page**: The top-level area (e.g., `dock`, `topbar`, `epic`, `feature`, `task`)
 * - **section**: The logical section within the page (e.g., `nav`, `header`, `tabs`)
 * - **element**: The specific interactive element (e.g., `dashboard`, `executeButton`)
 *
 * ## Usage
 *
 * ```tsx
 * import { HELP_IDS } from "@/lib/help-ids";
 *
 * <button data-help-id={HELP_IDS.dock.nav.dashboard}>Dashboard</button>
 * ```
 *
 * ## Adding New IDs
 *
 * 1. Add the new ID to the appropriate namespace in `HELP_IDS`
 * 2. Follow the `{page}.{section}.{element}` convention
 * 3. The `HelpId` type will automatically include the new value
 */

export const HELP_IDS = {
  /** Bottom dock navigation bar */
  dock: {
    /** Primary navigation links */
    nav: {
      dashboard: "dock.nav.dashboard",
      requests: "dock.nav.requests",
      epics: "dock.nav.epics",
      teams: "dock.nav.teams",
      admin: "dock.nav.admin",
      fogbugz: "dock.nav.fogbugz",
      ado: "dock.nav.ado",
      grooming: "dock.nav.grooming",
      toolbox: "dock.nav.toolbox",
    },
    /** Command palette search button */
    search: "dock.search",
    /** Chat toggle button */
    chat: "dock.chat",
    /** User avatar / settings button */
    avatar: "dock.avatar",
    /** Help center button */
    help: "dock.help",
  },

  /** Top bar header */
  topbar: {
    /** Back navigation button */
    backButton: "topbar.backButton",
    /** Logo link to dashboard */
    logo: "topbar.logo",
    /** Live clock display */
    clock: "topbar.clock",
  },

  /** Dashboard page */
  dashboard: {
    /** Activity chart card */
    activityChart: "dashboard.activityChart",
    /** Export button */
    exportButton: "dashboard.exportButton",
    /** Interval selector (Weekly/Monthly) */
    intervalSelector: "dashboard.intervalSelector",
    /** Metric cards grid */
    metrics: {
      features: "dashboard.metrics.features",
      tasks: "dashboard.metrics.tasks",
      decisions: "dashboard.metrics.decisions",
      sessions: "dashboard.metrics.sessions",
    },
    /** What's New card */
    whatsNew: "dashboard.whatsNew",
    /** Activity breakdown table */
    activityBreakdown: "dashboard.activityBreakdown",
  },

  /** Epic detail page */
  epic: {
    /** Header section */
    header: {
      /** Epic title (clickable to edit) */
      title: "epic.header.title",
      /** "New Feature" button */
      createFeature: "epic.header.createFeature",
      /** More options dropdown */
      moreMenu: "epic.header.moreMenu",
    },
    /** Description panel */
    description: {
      /** Collapsible description toggle */
      toggle: "epic.description.toggle",
      /** Description content area */
      content: "epic.description.content",
    },
    /** Execute section with orchestrator command */
    execute: {
      /** The orchestrator command code block */
      command: "epic.execute.command",
      /** Copy command button */
      copyButton: "epic.execute.copyButton",
    },
    /** Tab navigation */
    tabs: {
      /** Tab bar container */
      container: "epic.tabs.container",
      /** Overview tab */
      overview: "epic.tabs.overview",
      /** Plan tab */
      plan: "epic.tabs.plan",
      /** Session Monitor tab */
      monitor: "epic.tabs.monitor",
      /** Activity tab */
      activity: "epic.tabs.activity",
    },
    /** View mode toggle (list/board) */
    viewToggle: {
      /** List view button */
      list: "epic.viewToggle.list",
      /** Board view button */
      board: "epic.viewToggle.board",
    },
    /** Feature list / issue list area */
    featureList: "epic.featureList",
    /** Activity feed sidebar */
    activityFeed: "epic.activityFeed",
  },

  /** Feature detail page */
  feature: {
    /** Header section */
    header: {
      /** Feature identifier badge */
      identifier: "feature.header.identifier",
      /** Feature title */
      title: "feature.header.title",
      /** Delete button */
      deleteButton: "feature.header.deleteButton",
    },
    /** Properties sidebar */
    properties: {
      /** Status selector dropdown */
      status: "feature.properties.status",
      /** Assignee selector dropdown */
      assignee: "feature.properties.assignee",
    },
    /** Detail tabs */
    tabs: {
      /** Description tab content area */
      description: "feature.tabs.description",
      /** Activity tab */
      activity: "feature.tabs.activity",
      /** Decisions tab */
      decisions: "feature.tabs.decisions",
      /** Code context tab */
      code: "feature.tabs.code",
    },
    /** Task list section within description tab */
    taskList: "feature.taskList",
    /** Execute section with feature command */
    execute: {
      /** The orchestrator feature command code block */
      command: "feature.execute.command",
      /** Copy command button */
      copyButton: "feature.execute.copyButton",
    },
  },

  /** Epics list page */
  epics: {
    /** Active/Archived status filter dropdown */
    statusFilter: "epics.statusFilter",
    /** Card/List view toggle */
    viewToggle: "epics.viewToggle",
    /** Epic card grid */
    cardGrid: "epics.cardGrid",
  },

  /** Epic Requests list page */
  epicRequests: {
    /** "New Request" button (opens CLI instruction modal) */
    newRequest: "epicRequests.newRequest",
    /** Search input */
    search: "epicRequests.search",
    /** Status filter dropdown (Pending/Approved/etc.) */
    statusFilter: "epicRequests.statusFilter",
  },

  /** FogBugz ticket detail page */
  fogbugz: {
    /** Convert to Epic Request section */
    convertToER: {
      /** The command code block */
      command: "fogbugz.convertToER.command",
      /** Copy command button */
      copyButton: "fogbugz.convertToER.copyButton",
    },
  },

  /** Teams list page */
  teams: {
    /** Page title */
    title: "teams.title",
    /** Team card grid */
    cardGrid: "teams.cardGrid",
  },

  /** Task detail page */
  task: {
    /** Header section */
    header: {
      /** Task identifier badge */
      identifier: "task.header.identifier",
      /** Task title */
      title: "task.header.title",
      /** Delete button */
      deleteButton: "task.header.deleteButton",
    },
    /** Properties sidebar */
    properties: {
      /** Status selector dropdown */
      status: "task.properties.status",
      /** Assignee selector dropdown */
      assignee: "task.properties.assignee",
    },
    /** Task list item (inline in feature view) */
    listItem: {
      /** Checkbox to toggle completion */
      checkbox: "task.listItem.checkbox",
      /** Status select in expanded view */
      status: "task.listItem.status",
      /** Assignee select in expanded view */
      assignee: "task.listItem.assignee",
      /** Expand/collapse button */
      expandToggle: "task.listItem.expandToggle",
      /** More actions menu */
      moreMenu: "task.listItem.moreMenu",
    },
  },

  /** Onboarding & Help Center */
  onboarding: {
    /** Help Center modal container */
    modal: "onboarding.modal",
    /** Empty state CTA */
    emptyState: "onboarding.emptyState",
    /** What's Next card */
    whatsNext: "onboarding.whatsNext",
    /** Platform selection cards */
    platformSelect: "onboarding.platformSelect",
    /** Prerequisites checklist */
    prerequisites: "onboarding.prerequisites",
    /** CLI setup wizard */
    cliSetup: "onboarding.cliSetup",
  },

  /** Help Center */
  helpCenter: {
    /** Main menu view */
    menu: "helpCenter.menu",
    /** Flow card in menu */
    flowCard: "helpCenter.flowCard",
    /** Wizard step view */
    wizard: "helpCenter.wizard",
    /** Step progress indicator */
    stepIndicator: "helpCenter.stepIndicator",
  },
} as const;

/**
 * Recursively extracts all leaf string values from a nested const object.
 * Used to create a union type of all valid help IDs.
 */
type LeafValues<T> = T extends string
  ? T
  : T extends Record<string, unknown>
    ? { [K in keyof T]: LeafValues<T[K]> }[keyof T]
    : never;

/**
 * Union type of all valid help ID strings.
 *
 * This type is automatically derived from `HELP_IDS` and will include
 * any new IDs added to the const object.
 *
 * @example
 * ```ts
 * function highlightElement(id: HelpId) {
 *   document.querySelector(`[data-help-id="${id}"]`);
 * }
 * ```
 */
export type HelpId = LeafValues<typeof HELP_IDS>;

/** Runtime set of all valid HelpId values for input validation. */
function collectLeafValues(obj: Record<string, unknown>): Set<string> {
  const values = new Set<string>();
  for (const v of Object.values(obj)) {
    if (typeof v === "string") values.add(v);
    else if (v && typeof v === "object") {
      for (const s of collectLeafValues(v as Record<string, unknown>)) values.add(s);
    }
  }
  return values;
}
export const VALID_HELP_IDS: ReadonlySet<string> = collectLeafValues(HELP_IDS);
